import type { PageMeta } from '@/types/api';

import type { RequestParams, RequestResult, SortParam } from './types';

/** The API list-params shape every paginated endpoint accepts: 1-based `page`,
 *  `limit`, and optional sort. Feature filter fields are spread on top. */
export type ListParams<F> = {
  page: number;
  limit: number;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
} & F;

/** Map DataTable's sort → the API's `sortBy`/`sortOrder` (antd `ascend/descend` → `asc/desc`).
 *  Shared by the list request and a page's export, so an export asks for the order on screen
 *  (ADO 120974). An unsorted table sends neither. */
export function toSortParams(sort: SortParam): Pick<ListParams<object>, 'sortBy' | 'sortOrder'> {
  return {
    sortBy: sort?.field,
    sortOrder: sort ? (sort.order === 'ascend' ? 'asc' : 'desc') : undefined,
  };
}

/** Map DataTable's request params → API list params. Feature-agnostic: renames
 *  `pageSize→limit`, lifts `sort.field→sortBy`, and maps antd's `ascend/descend`
 *  to the API's `asc/desc`. Filters pass through untouched. */
export function toListParams<F extends Record<string, unknown>>(
  p: RequestParams<F>,
): ListParams<F> {
  return {
    ...p.filters,
    page: p.page,
    limit: p.pageSize,
    ...toSortParams(p.sort),
  };
}

/** Map an API list response → DataTable's `{ data, total }` result. */
export function toResult<Row>(res: { data: Row[]; meta: PageMeta }): RequestResult<Row> {
  return { data: res.data, total: res.meta.totalItems };
}

/** Build a DataTable `request` prop from a feature list function. The feature
 *  `listFn` owns its own filter→query mapping; this only bridges the table↔API
 *  paging/sort/result contract. Define once at module scope per page. */
export function makeListRequest<Row, F extends Record<string, unknown>>(
  listFn: (params: ListParams<F>) => Promise<{ data: Row[]; meta: PageMeta }>,
): (p: RequestParams<F>) => Promise<RequestResult<Row>> {
  return async (p) => toResult(await listFn(toListParams(p)));
}
